import { auth, getIdToken, signOut as googleSignOut } from '../auth/firebase';
import { noteSessionEnded, passwordSession, setPasswordSession } from '../auth/session';
import { config } from '../config';

/**
 * The one fetch site. sphere-watch answers with real HTTP status codes -- unlike
 * sphere-crm, whose always-200 envelope exists to match dispatch-be's wire
 * contract -- so status is meaningful here and is used.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Dev-only fixture seam, mirroring the CRM console's setFixtureResolver.
 *
 * The screens on this console are hard to look at otherwise: they need a signed-in
 * staff identity AND a backend with real incidents, so a layout regression would
 * only ever be caught in production, on the screen someone is reading at 3am.
 */
type FixtureResolver = (method: string, path: string, body?: unknown) => unknown | undefined;
let fixtures: FixtureResolver | null = null;
export const setFixtureResolver = (resolver: FixtureResolver | null) => {
  fixtures = resolver;
};

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  if (fixtures) {
    // The body too, so a fixture can play a write back on the next read.
    const canned = fixtures(method, path, body);
    if (canned !== undefined) return canned as T;
  }
  // A password session when there is one, else Google's: the sign-in screen
  // only ever makes one of the two.
  const session = passwordSession();
  const googleUid = session ? null : (auth.currentUser?.uid ?? null);
  const token = session ? session.token : await getIdToken();
  const response = await fetch(`${config.apiRoot}${path}`, {
    method,
    headers: {
      accept: 'application/json',
      ...(body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(response.status, `${response.status}: unreadable response`);
  }
  if (!response.ok) {
    // The server ended this password session -- its hours are up, the
    // password was changed, access was turned off. Dropping it is what puts
    // the sign-in screen back, with why. Only if it is still the one in use:
    // a late answer must not sign out a newer session.
    if (response.status === 401 && session && passwordSession()?.token === session.token) {
      setPasswordSession(null, 'Your sign-in has ended. Sign in again.');
    }
    // A Google sign-in from before this person's password was changed or
    // their access turned off. Firebase would go on renewing it, so it is
    // signed out here -- the same person, still, or a late answer must not
    // sign out whoever came in since.
    const code = (payload as { code?: string } | null)?.code;
    if (response.status === 401 && code === 'session_ended' && googleUid && auth.currentUser?.uid === googleUid) {
      noteSessionEnded('Your sign-in has ended. Sign in again.');
      void googleSignOut();
    }
    const error = (payload as { error?: string } | null)?.error;
    throw new ApiError(response.status, error || `${response.status}`);
  }
  return payload as T;
}

/**
 * No `put`, and no `patch`. The API is on another origin, and nginx answers
 * the browser's preflight from a fixed list -- GET, POST and DELETE
 * (deploy/nginx) -- so any other method is refused by the browser before it is
 * sent, while the same call works from curl. Saving the desk's hours shipped
 * that way. A change goes as a POST; scripts/verify/staff.js holds the server
 * to the same list.
 */
export const api = {
  /**
   * `signal` aborts the fetch, and a server that watches for it stops the
   * work behind it: /audit cancels its statement on dispatch's database when
   * the request closes, so a question nobody is waiting for any more stops
   * holding the one connection audit reads are allowed.
   */
  get: <T>(path: string, init?: { signal?: AbortSignal }) => request<T>('GET', path, undefined, init?.signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
